// Packages
import React from "react"
import PropTypes from "prop-types"
import { CSSTransition } from "react-transition-group"

// Utils
import { setPid } from "lib/navigation-util"

// Config
import PRConfig from "components/Stacker/config/ProductConfiguratorConfig"

// Actions
import {
  setMetal,
  setTheme,
  setConfigData,
} from "components/Stacker/actions/ProductConfiguratorActions"
import { getProductDetails } from "components/Stacker/actions/ProductDetailActions"

// Components
import ProductConfigHeader from "components/Stacker/components/ProductConfigurator/ProductConfigHeader/ProductConfigHeader"
import ProductConfigMenu from "components/Stacker/components/ProductConfigurator/ProductConfigMenu/ProductConfigMenu"
import Step1 from "components/Stacker/components/ProductConfigurator/Step1/Step1"
import Step2 from "components/Stacker/components/ProductConfigurator/Step2/Step2"
import Step3Motif from "components/Stacker/components/ProductConfigurator/Step3Motif/Step3Motif"
import Step3Monogram from "components/Stacker/components/ProductConfigurator/Step3Monogram/Step3Monogram"
import Step4Confirm from "components/Stacker/components/ProductConfigurator/Step4Confirm/Step4Confirm"

// Styles
import "./ProductConfiguratorPage.scss"
import shadowDOM from "components/Stacker/utils/shadowDOM"

import { useState } from "react"
import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useRef } from "react"
import { useContext } from "react"
import { StackerContext } from "components/DYStackers/StackerContext"
import { useSelector } from "react-redux"
import { useCallback } from "react"

function ProductConfiguratorPage({
  isQuickView,
  swatch,
  configProductSet,
  configurableProductType,
  productTheme,
  theme,
}) {
  const [transitionValue, set_transitionValue] = useState(
    PRConfig.PRC_SLIDE_TRANSITION
  )
  const [headerHeight, set_headerHeight] = useState(100)
  const configHeader = useRef()
  const configStepContainerNode = useRef()
  const dispatch = useDispatch()
  const stackerContext = useContext(StackerContext)

  const { stateInfo, baseStore } = useSelector((state) => {
    return {
      stateInfo: state.productConfig,
      baseStore: state.application.baseStore,
    }
  })

  useEffect(() => {
    dispatch(setTheme(productTheme))
  }, [productTheme, dispatch])

  useEffect(() => {
    dispatch(
      setConfigData({
        data: configProductSet,
        configTypeValue: PRConfig[configurableProductType].PRODUCT_TYPE,
      })
    )
  }, [configProductSet, dispatch, configurableProductType])

  const setScroll = (elem) => {
    if (elem) {
      window.scrollTo({
        behavior: "smooth",
        left: 0,
        top: elem.offsetTop,
      })
    } else {
      window.scrollTo(0, 0)
    }
  }

  const setContainerHeight = useCallback(
    (x) => {
      shadowDOM.getElementById("step-title").style.paddingTop = headerHeight
      const h = parseInt(x, 10)
      shadowDOM.getElementById("stepHeightContainer").style.height = `${h}px`
    },
    [headerHeight]
  )

  const nextStep = useCallback(
    (e) => {
      set_transitionValue(PRConfig.PRC_SLIDE_TRANSITION)
      dispatch(nextStep())
    },
    [dispatch]
  )

  const setMetal = useCallback(
    (e) => {
      set_transitionValue(PRConfig.PRC_SLIDE_TRANSITION)
      const { metal } = e.target.dataset

      const data = {
        metal,
      }

      const selectedMetalProduct = configProductSet.filter(
        (product) => metal === product.METAL_VALUE
      )
      const pid = selectedMetalProduct[0].PRODUCT_ID

      const selectedSwatch = Object.keys(swatch).length > 0 ? swatch[pid] : {}

      setPid(pid)
      dispatch(
        getProductDetails({
          args: {
            productId: pid,
            baseStore,
            selectedSwatch,
            isQuickView,
          },
          retailerId: stackerContext.retailerId,
        })
      )

      dispatch(setMetal(data))
      nextStep()
    },
    [
      baseStore,
      configProductSet,
      dispatch,
      isQuickView,
      nextStep,
      stackerContext.retailerId,
      swatch,
    ]
  )

  const setDesign = (e) => {
    set_transitionValue(PRConfig.PRC_SLIDE_TRANSITION)
    const data = {
      design: e.target.value,
    }
    dispatch(setDesign(data))
    nextStep()
  }

  const setMotif = (e) => {
    const {
      value,
      dataset: { motifcode },
    } = e.currentTarget
    const data = {
      icon: value,
      motifCode: motifcode,
    }
    dispatch(setMotif(data))
  }

  const prevStep = useCallback(
    (e) => {
      set_transitionValue(PRConfig.PRC_SLIDEBACK_TRANSITION)
      dispatch(
        prevStep({
          stateInfo,
          baseStore,
          isQuickView,
          swatch,
          configProductSet,
          configurableProductType,
          productTheme,
          theme,
        })
      )
    },
    [
      baseStore,
      configProductSet,
      configurableProductType,
      dispatch,
      isQuickView,
      productTheme,
      stateInfo,
      swatch,
      theme,
    ]
  )

  const showStep = () => {
    const { currentStep, design } = stateInfo
    switch (true) {
      case currentStep === 1:
        return (
          <Step1
            key={1}
            {...stateInfo}
            setContainerHeight={setContainerHeight}
            setMetalStep={setMetal}
            configProductSet={configProductSet}
          />
        )
      case currentStep === 2 && !theme:
        return (
          <Step2
            key={2}
            {...stateInfo}
            setContainerHeight={setContainerHeight}
            setDesignStep={setDesign}
          />
        )
      case currentStep === 3: {
        let temp = ""
        if (design === PRConfig.DESIGN_VALUES[0]) {
          temp = (
            <Step3Monogram
              key={3}
              {...stateInfo}
              setContainerHeight={setContainerHeight}
              nextStep={nextStep}
              containerNode={configStepContainerNode.current}
              setScroll={setScroll}
            />
          )
        } else if (design === PRConfig.DESIGN_VALUES[1]) {
          temp = (
            <Step3Motif
              key={3}
              {...stateInfo}
              setContainerHeight={setContainerHeight}
              setMotifStep={setMotif}
              nextStep={nextStep}
              containerNode={configStepContainerNode.current}
              setScroll={setScroll}
            />
          )
        }
        return temp
      }
      case currentStep === 4:
        return (
          <Step4Confirm
            key={4}
            {...stateInfo}
            setContainerHeight={setContainerHeight}
            containerNode={configStepContainerNode.current}
            setScroll={setScroll}
            configProductSet={configProductSet}
          />
        )
      default: {
        return PRConfig.DEFAULT_VALUE
      }
    }
  }

  const { currentStep } = stateInfo
  const configTypeValue = PRConfig[configurableProductType].PRODUCT_TYPE
  return (
    <article
      className={`ProductConfiguratorPage ${configTypeValue} prc-step-${currentStep}`}
    >
      <div
        className="configStepContainer"
        id="stepHeightContainer"
        ref={configStepContainerNode}
      >
        <ProductConfigHeader
          ref={configHeader}
          configTypeValue={configTypeValue}
          getHeaderHeight={set_headerHeight}
          {...stateInfo}
          transitionFunc={prevStep}
        />
        <div className="config-content-container">
          <CSSTransition timeout={700} classNames={transitionValue}>
            {showStep()}
          </CSSTransition>
        </div>
      </div>
      <ProductConfigMenu
        swatch={swatch}
        baseStore={baseStore}
        isQuickView={isQuickView}
        {...stateInfo}
        configProductSet={configProductSet}
      />
    </article>
  )
}

ProductConfiguratorPage.propTypes = {
  metal: PropTypes.string,
  design: PropTypes.string,
  currentStep: PropTypes.number,
  isQuickView: PropTypes.bool,
  swatch: PropTypes.object,
  theme: PropTypes.string,
  productTheme: PropTypes.string,
  configProductSet: PropTypes.array,
  configurableProductType: PropTypes.string,
}

export default ProductConfiguratorPage
